/**
 * ClauseList — displays a list of extracted standard clauses grouped by section.
 *
 * Main-body clauses (4, 5, 6 …) are grouped by their top-level number.
 * Annex clauses (A.x, B.x …) are grouped by their annex letter.
 */

import { useTranslation } from "@/packages/utils";
import { findProfileByCode, type AnnexProfile } from "@/packages/standards";
import type { Doc } from "convex/_generated/dataModel";

interface ClauseListProps {
  clauses: Doc<"clauses">[];
}

interface ClauseGroup {
  key: string;
  label: string;
  clauses: Doc<"clauses">[];
}

/** Parse "A.5.1.2" → [NaN, 5, 1, 2] or "7.2" → [7, 2] for numeric comparison */
function clauseSortKey(clauseNumber: string): number[] {
  return clauseNumber.split(".").map(Number);
}

function compareClauseNumbers(a: string, b: string): number {
  const ka = clauseSortKey(a);
  const kb = clauseSortKey(b);
  for (let i = 0; i < Math.max(ka.length, kb.length); i++) {
    const va = ka[i] ?? -1;
    const vb = kb[i] ?? -1;
    // If both are NaN (letter prefixes like "A"), compare as strings
    if (Number.isNaN(va) && Number.isNaN(vb)) {
      const sa = a.split(".")[i] ?? "";
      const sb = b.split(".")[i] ?? "";
      const cmp = sa.localeCompare(sb);
      if (cmp !== 0) return cmp;
      continue;
    }
    if (Number.isNaN(va)) return -1;
    if (Number.isNaN(vb)) return 1;
    if (va !== vb) return va - vb;
  }
  return 0;
}

function groupClauses(clauses: Doc<"clauses">[]): ClauseGroup[] {
  const groups = new Map<string, Doc<"clauses">[]>();
  const groupOrder: string[] = [];

  for (const clause of clauses) {
    const annexMatch = clause.clauseNumber.match(/^([A-Z])\./);
    const key = annexMatch ? annexMatch[1] : clause.clauseNumber.split(".")[0];

    const existing = groups.get(key);
    if (existing) {
      existing.push(clause);
    } else {
      groups.set(key, [clause]);
      groupOrder.push(key);
    }
  }

  // Sort clauses within each group by clauseNumber
  for (const group of groups.values()) {
    group.sort((a, b) => compareClauseNumbers(a.clauseNumber, b.clauseNumber));
  }

  // Sort: numeric sections first (ascending), then annex letters (ascending)
  groupOrder.sort((a, b) => {
    const aIsNum = /^\d+$/.test(a);
    const bIsNum = /^\d+$/.test(b);
    if (aIsNum && bIsNum) return Number(a) - Number(b);
    if (aIsNum) return -1;
    if (bIsNum) return 1;
    return a.localeCompare(b);
  });

  // Resolve annex labels from the standard profile (use first clause's standardCode)
  const firstClause = clauses[0];
  const profile = firstClause?.standardCode
    ? findProfileByCode(firstClause.standardCode)
    : undefined;

  const annexMap = new Map<string, AnnexProfile>();
  if (profile) {
    for (const annex of profile.annexes) {
      annexMap.set(annex.annexId, annex);
    }
  }

  return groupOrder.map((key) => {
    const groupClauses = groups.get(key) ?? [];
    const annexProfile = annexMap.get(key);

    let label: string;
    if (annexProfile) {
      label = annexProfile.label;
    } else {
      // Use the top-level clause title if it exists (e.g. clause "7" → "7 — Support")
      const topClause = groupClauses.find(
        (c) => c.clauseNumber === key && !c.parentClause,
      );
      label = topClause ? `${key} — ${topClause.title}` : `Section ${key}`;
    }

    return { key, label, clauses: groupClauses };
  });
}

export function ClauseList({ clauses }: ClauseListProps) {
  const { t } = useTranslation(["common"]);

  if (clauses.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 text-center text-sm text-muted">
        {t("emptyStates.noClausesExtracted")}
      </div>
    );
  }

  const groups = groupClauses(clauses);

  return (
    <div className="space-y-4">
      {groups.map((group) => (
        <div
          key={group.key}
          className="rounded-xl border border-border overflow-hidden"
        >
          <div className="px-4 py-2.5 bg-muted/40 border-b border-border">
            <h3 className="text-sm font-semibold text-primary">
              {group.label}
            </h3>
          </div>
          {group.clauses.map((clause) => (
            <div
              key={clause._id}
              className={`border-b border-border/60 px-4 py-3 last:border-0 ${
                clause.parentClause ? "pl-8 bg-card/30" : "bg-card"
              }`}
            >
              <div className="text-sm font-medium text-primary">
                {clause.clauseNumber} — {clause.title}
              </div>
              <div className="mt-1 text-sm text-secondary line-clamp-3">
                {clause.description}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
